import type { TreeDataNode } from 'antd';
import { CROP_KEY_PREFIX, FARM_KEY_PREFIX } from 'constants/routeConstants';
import {
  COPY_TOOL_EMPTY_PADDOCKS_GROUP_DISABLED_TOOLTIP,
  COPY_TOOL_GROUP_DISABLED_TOOLTIP,
  COPY_TOOL_PADDOCK_TAKEN_DISABLED_TOOLTIP,
} from 'constants/uiConstants';
import { IInput } from 'interfaces/IInput';

import { COPY_NO_PLANS_GROUP_KEY } from './buildCopyCropTreeData';
import {
  CopyTreeIndex,
  disableCopyTreeNode,
  getCopyPaddockKey,
  getInputKeysById,
} from './buildCopyTreeData';
import {
  applySingleCopyCheck,
  CopyTreeCheckedKeys,
  normaliseCopyKeys,
  withLinkedCopies,
} from './copySelection';

/** What a tick on a shown row needs to know about its panel. */
export interface ShownCheckContext {
  /** The panel's selection index (on Destination, without the Source). */
  index: CopyTreeIndex;
  allowMultiple: boolean;
  paddocksOnly: boolean;
  /** Paddocks a search leaves shown; null when not searching. */
  shownPaddockKeys: ReadonlySet<string> | null;
}

/**
 * Add as new: the document (by input id, as a search can change a linked
 * document's row key) whose tick added each paddock, by paddock key.
 */
export type PaddockOwners = Record<string, string>;

type ShownCheckState = 'checked' | 'half' | 'unchecked' | 'disabled';

const isTickable = (node: TreeDataNode) =>
  node.checkable !== false && !node.disableCheckbox;

/**
 * Rows whose tick is the rows shown under them: farms, crops, the paddocks
 * without plans group, and a paddock with documents (its documents are the
 * targets, except in Add as new, where the paddock is).
 */
const isGroupNode = (
  node: TreeDataNode,
  { index, paddocksOnly }: ShownCheckContext
) => {
  const key = String(node.key);
  if (
    key.startsWith(FARM_KEY_PREFIX) ||
    key.startsWith(CROP_KEY_PREFIX) ||
    key === COPY_NO_PLANS_GROUP_KEY
  ) {
    return true;
  }
  const info = index.get(key);
  return info?.kind === 'paddock' && !info.isEmpty && !paddocksOnly;
};

/**
 * Whether a group's tick reaches this child. A farm skips its empty paddocks
 * outside Add as new, so a farm tick never creates documents by itself; those
 * are ticked on their own or through the paddocks without plans group.
 */
const reachesChild = (
  group: TreeDataNode,
  child: TreeDataNode,
  context: ShownCheckContext
) => {
  if (context.paddocksOnly || !String(group.key).startsWith(FARM_KEY_PREFIX)) {
    return true;
  }
  const info = context.index.get(String(child.key));
  return !(info?.kind === 'paddock' && info.isEmpty);
};

// Built once per tree, as a document's paddocks are read for every row on every tick.
const paddockKeysCache = new WeakMap<CopyTreeIndex, Map<string, string[]>>();

/** The paddocks a document is in, as paddock keys of this tree. */
export const getInputPaddockKeys = (
  input: IInput,
  index: CopyTreeIndex
): string[] => {
  let byId = paddockKeysCache.get(index);
  if (!byId) {
    byId = new Map();
    paddockKeysCache.set(index, byId);
  }
  const cached = byId.get(input.id);
  if (cached) return cached;
  const keys = Array.from(
    new Set(
      (getInputKeysById(index).get(input.id) ?? []).flatMap((key) => {
        const info = index.get(key);
        return info?.kind === 'input'
          ? [getCopyPaddockKey(info.paddockId)]
          : [];
      })
    )
  );
  byId.set(input.id, keys);
  return keys;
};

// Add as new: a document stands for its paddocks, limited to those a search shows.
const getShownInputPaddockKeys = (
  input: IInput,
  { index, shownPaddockKeys }: ShownCheckContext
) =>
  getInputPaddockKeys(input, index).filter(
    (key) => !shownPaddockKeys || shownPaddockKeys.has(key)
  );

/** Every shown node by key, so a tick can reach the rows under it. */
export const indexShownNodes = (
  treeData: TreeDataNode[]
): Map<string, TreeDataNode> => {
  const byKey = new Map<string, TreeDataNode>();
  const walk = (nodes: TreeDataNode[]) =>
    nodes.forEach((node) => {
      byKey.set(String(node.key), node);
      walk(node.children ?? []);
    });
  walk(treeData);
  return byKey;
};

/**
 * Add as new: who holds each ticked paddock shown in Crop view. A paddock's
 * owner is the document recorded as ticking it, while that document is shown,
 * else the first document shown in it (e.g. after a Farm view tick).
 */
export interface AddAsNewOwnership {
  ownerOf: Map<string, string>;
  /** Each tickable document's shown paddocks. */
  docPaddocks: Map<string, string[]>;
}

/** Null outside Add as new, where documents are the targets. */
export const resolveAddAsNewOwnership = (
  treeData: TreeDataNode[],
  keys: string[],
  owners: PaddockOwners,
  context: ShownCheckContext
): AddAsNewOwnership | null => {
  if (!context.paddocksOnly) return null;
  const docsByPaddock = new Map<string, string[]>();
  const docPaddocks = new Map<string, string[]>();
  const collect = (nodes: TreeDataNode[]) =>
    nodes.forEach((node) => {
      const key = String(node.key);
      const info = context.index.get(key);
      if (info?.kind === 'input' && isTickable(node)) {
        const paddockKeys = getShownInputPaddockKeys(info.input, context);
        docPaddocks.set(key, paddockKeys);
        paddockKeys.forEach((paddockKey) => {
          const docs = docsByPaddock.get(paddockKey);
          if (docs) docs.push(key);
          else docsByPaddock.set(paddockKey, [key]);
        });
      }
      collect(node.children ?? []);
    });
  collect(treeData);

  const ownerOf = new Map<string, string>();
  keys.forEach((paddockKey) => {
    const docs = docsByPaddock.get(paddockKey);
    if (!docs) return;
    const recordedId = owners[paddockKey];
    const recorded = docs.find((docKey) => {
      const info = context.index.get(docKey);
      return info?.kind === 'input' && info.input.id === recordedId;
    });
    ownerOf.set(paddockKey, recorded ?? docs[0]);
  });
  return { ownerOf, docPaddocks };
};

const getOwnedPaddocks = (docKey: string, ownership: AddAsNewOwnership) =>
  (ownership.docPaddocks.get(docKey) ?? []).filter(
    (paddockKey) => ownership.ownerOf.get(paddockKey) === docKey
  );

interface CheckTargets {
  inputKeys: string[];
  paddockKeys: string[];
  /** Add as new: the document claiming each paddock in this tick. */
  owners: PaddockOwners;
}

const collectTargets = (
  node: TreeDataNode,
  checked: boolean,
  context: ShownCheckContext,
  ownership: AddAsNewOwnership | null,
  targets: CheckTargets
) => {
  if (!isTickable(node)) return;
  if (isGroupNode(node, context)) {
    (node.children ?? [])
      .filter((child) => reachesChild(node, child, context))
      .forEach((child) =>
        collectTargets(child, checked, context, ownership, targets)
      );
    return;
  }
  const key = String(node.key);
  const info = context.index.get(key);
  if (info?.kind === 'paddock') targets.paddockKeys.push(key);
  if (info?.kind !== 'input') return;
  if (!context.paddocksOnly || !ownership) {
    targets.inputKeys.push(key);
    return;
  }
  // One document per paddock: an untick frees what it owns, a tick claims only free paddocks.
  if (!checked) {
    targets.paddockKeys.push(...getOwnedPaddocks(key, ownership));
    return;
  }
  (ownership.docPaddocks.get(key) ?? []).forEach((paddockKey) => {
    const owner = ownership.ownerOf.get(paddockKey);
    if (targets.owners[paddockKey] || (owner && owner !== key)) return;
    targets.paddockKeys.push(paddockKey);
    targets.owners[paddockKey] = info.input.id;
  });
};

/**
 * One tick on a shown row. A group ticks the tickable rows shown under it, so
 * a search narrows what it reaches; the keys are gathered first and fitted to
 * the panel once, however many rows there are. In Add as new a document's
 * tick claims its free paddocks and records itself as their owner.
 */
export const applyShownCheck = (
  keys: string[],
  owners: PaddockOwners,
  node: TreeDataNode,
  checked: boolean,
  context: ShownCheckContext,
  ownership: AddAsNewOwnership | null
): { keys: string[]; owners: PaddockOwners } => {
  const { index, allowMultiple, paddocksOnly } = context;
  if (!allowMultiple) {
    return {
      keys: applySingleCopyCheck(keys, String(node.key), checked, index),
      owners,
    };
  }

  const targets: CheckTargets = { inputKeys: [], paddockKeys: [], owners: {} };
  collectTargets(node, checked, context, ownership, targets);
  const toggled = new Set([
    ...withLinkedCopies(targets.inputKeys, index),
    ...targets.paddockKeys,
  ]);
  const remaining = keys.filter((key) => !toggled.has(key));
  const next = checked ? [...remaining, ...Array.from(toggled)] : remaining;

  const nextOwners = { ...owners };
  targets.paddockKeys.forEach((paddockKey) => {
    delete nextOwners[paddockKey];
  });
  if (checked) Object.assign(nextOwners, targets.owners);

  return {
    keys: paddocksOnly ? next : normaliseCopyKeys(next, index, true),
    owners: nextOwners,
  };
};

/**
 * Add as new: the documents to grey out, which own no ticked paddock and have
 * no free paddock to claim, as all of its paddocks are owned by others. An owner
 * is never greyed out, so every ticked paddock keeps a document that can
 * untick it.
 */
export const getBlockedAddAsNewDocuments = (
  ownership: AddAsNewOwnership | null
): string[] => {
  if (!ownership) return [];
  // Nothing it owns and nothing free to claim; a linked document with a free paddock stays tickable.
  return Array.from(ownership.docPaddocks.entries())
    .filter(
      ([docKey, paddockKeys]) =>
        getOwnedPaddocks(docKey, ownership).length === 0 &&
        paddockKeys.every((paddockKey) => ownership.ownerOf.has(paddockKey))
    )
    .map(([docKey]) => docKey);
};

/** Greys out the blocked Add as new documents, copying only the rows on their path. */
export const fitAddAsNewDocuments = (
  treeData: TreeDataNode[],
  blockedKeys: ReadonlySet<string>
): TreeDataNode[] => {
  if (blockedKeys.size === 0) return treeData;

  const fit = (nodes: TreeDataNode[]): TreeDataNode[] => {
    let changed = false;
    const fitted = nodes.map((node) => {
      const key = String(node.key);
      if (blockedKeys.has(key)) {
        changed = true;
        return disableCopyTreeNode(
          node,
          COPY_TOOL_PADDOCK_TAKEN_DISABLED_TOOLTIP
        );
      }
      if (!node.children) return node;
      const children = fit(node.children);
      if (children === node.children) return node;
      changed = true;
      return { ...node, children };
    });
    return changed ? fitted : nodes;
  };
  return fit(treeData);
};

/**
 * The ticks as shown: a group is ticked when every row its tick reaches is,
 * half-ticked when some are, so ticks hidden by a search don't show; in Add
 * as new a document is ticked when it owns a paddock and has none left free.
 */
export const getShownCheckedKeys = (
  treeData: TreeDataNode[],
  base: CopyTreeCheckedKeys,
  context: ShownCheckContext,
  ownership: AddAsNewOwnership | null
): CopyTreeCheckedKeys => {
  const checked = new Set(base.checked);
  const halfChecked = new Set(base.halfChecked);

  const mark = (key: string, state: ShownCheckState) => {
    checked.delete(key);
    halfChecked.delete(key);
    if (state === 'checked') checked.add(key);
    if (state === 'half') halfChecked.add(key);
    return state;
  };

  const visit = (node: TreeDataNode): ShownCheckState => {
    const key = String(node.key);
    const childStates = (node.children ?? []).map(
      (child) => [child, visit(child)] as const
    );
    const isGroup = isGroupNode(node, context);
    if (!isTickable(node)) {
      // A greyed-out group shows no tick, even for rows a search hides.
      if (isGroup && node.disableCheckbox) mark(key, 'unchecked');
      return 'disabled';
    }

    if (isGroup) {
      const states = childStates
        .filter(([child]) => reachesChild(node, child, context))
        .map(([, state]) => state)
        .filter((state) => state !== 'disabled');
      if (states.length === 0) return mark(key, 'unchecked');
      if (states.every((state) => state === 'checked')) {
        return mark(key, 'checked');
      }
      return mark(
        key,
        states.some((state) => state !== 'unchecked') ? 'half' : 'unchecked'
      );
    }

    if (ownership && ownership.docPaddocks.has(key)) {
      // Ticked once it owns a paddock and none of its paddocks is still free; another's isn't free.
      const owned = getOwnedPaddocks(key, ownership);
      const hasFreePaddock = (ownership.docPaddocks.get(key) ?? []).some(
        (paddockKey) => !ownership.ownerOf.has(paddockKey)
      );
      if (owned.length === 0) return mark(key, 'unchecked');
      return mark(key, hasFreePaddock ? 'half' : 'checked');
    }

    if (checked.has(key)) return 'checked';
    return halfChecked.has(key) ? 'half' : 'unchecked';
  };

  treeData.forEach(visit);
  return { checked: Array.from(checked), halfChecked: Array.from(halfChecked) };
};

/** Greys out a group with nothing its tick can reach, so it isn't a dead control. */
export const disableEmptyGroups = (
  treeData: TreeDataNode[],
  context: ShownCheckContext
): TreeDataNode[] => {
  const fit = (node: TreeDataNode): [TreeDataNode, boolean] => {
    if (!isGroupNode(node, context)) return [node, isTickable(node)];
    const fitted = (node.children ?? []).map(fit);
    const hasTarget = fitted.some(
      ([child, target]) => target && reachesChild(node, child, context)
    );
    const children = fitted.map(([child]) => child);
    const changed = children.some((child, i) => child !== node.children?.[i]);
    if (hasTarget || !isTickable(node)) {
      return [changed ? { ...node, children } : node, hasTarget];
    }
    // Only tickable rows its tick skips, i.e. a farm's paddocks without plans.
    const skipsTargets = fitted.some(([, target]) => target);
    return [
      disableCopyTreeNode(
        { ...node, children },
        skipsTargets
          ? COPY_TOOL_EMPTY_PADDOCKS_GROUP_DISABLED_TOOLTIP
          : COPY_TOOL_GROUP_DISABLED_TOOLTIP
      ),
      false,
    ];
  };
  return treeData.map((node) => fit(node)[0]);
};
